import { z } from 'zod';

/**
 * What a tool's result touched, for the conversation's previews and its Summary
 * (`brand/crow-extensions.html`, "Ce qu'une conversation touche"):
 *
 * - `artifact`: a typed file Crowdex indexes (a design, a sheet, a workflow), opened in its editor;
 * - `item`: a thing of an app or a service that lives there (a CrowDrive file, a CrowBox mail, a
 *   pull request, an issue, a Slack thread), opened in its app or at its service;
 * - `work`: what an assistant does (a task, a workflow's run), read only.
 */
export const TOOL_REF_KINDS = ['artifact', 'item', 'work'] as const;
export type ToolRefKind = (typeof TOOL_REF_KINDS)[number];

/** What the tool did with it: the Summary says "Made", "Changed", "Read" or "Sent". */
export const TOOL_REF_ACTIONS = ['read', 'made', 'changed', 'sent'] as const;
export type ToolRefAction = (typeof TOOL_REF_ACTIONS)[number];

/**
 * Where a tool result carries its refs: `_meta["crowdex/refs"]` of an MCP result (one or a list),
 * which leaves its content as it is. A plugin's tool may also answer `{ ..., ref }` (or `refs`)
 * in its structured content (`withRef` of the SDK).
 */
export const TOOL_REFS_META_KEY = 'crowdex/refs';

/**
 * Where a tool's definition (its `tools/list` entry) says the user approves each of its calls,
 * whatever the conversation allows: `_meta["crowdex/approval"]: "always"` (a tool that sends a
 * mail for the user). Code mode asks before every call.
 */
export const TOOL_APPROVAL_META_KEY = 'crowdex/approval';

/** At most this many refs are read from one tool result; the rest are dropped. */
export const MAX_TOOL_REFS = 20;

const webUrl = z
  .string()
  .max(2048)
  .refine((value) => {
    try {
      const url = new URL(value);
      return url.protocol === 'https:' || url.protocol === 'http:';
    } catch {
      return false;
    }
  }, 'An http(s) URL.');

/** An image the preview shows: a web URL, or a small inline one (`data:image/…`, under 200 KB). */
const previewImage = z
  .string()
  .max(200_000)
  .refine(
    (value) =>
      /^data:image\/(png|jpeg|webp|gif);base64,/.test(value) || webUrl.safeParse(value).success,
    'An http(s) URL or a data:image URL.',
  );

/**
 * What the host draws in the preview's 140 px body when the extension has no preview of its own:
 * an image (a thumbnail, a page), the first rows of a table, or a few lines of text (a mail).
 */
export const toolRefPreviewSchema = z.object({
  text: z.string().max(2_000).optional(),
  image: previewImage.optional(),
  rows: z
    .array(z.array(z.string().max(200)).max(12))
    .max(8)
    .optional(),
});
export type ToolRefPreview = z.infer<typeof toolRefPreviewSchema>;

/**
 * What a type's preview route answered (`types[].preview`), as a card draws it: its rows and
 * text clipped to the card's limits, anything else dropped. Nothing usable: null.
 */
export function typePreviewOf(value: unknown): ToolRefPreview | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const answer = value as Record<string, unknown>;
  const rows = Array.isArray(answer['rows'])
    ? answer['rows']
        .slice(0, 8)
        .filter((row): row is unknown[] => Array.isArray(row))
        .map((row) => row.slice(0, 12).map((cell) => String(cell ?? '').slice(0, 200)))
    : undefined;
  const text = typeof answer['text'] === 'string' ? answer['text'].slice(0, 2_000) : undefined;
  const parsed = toolRefPreviewSchema.safeParse({
    ...(typeof answer['image'] === 'string' ? { image: answer['image'] } : {}),
    ...(rows?.some((row) => row.some((cell) => cell !== '')) ? { rows } : {}),
    ...(text !== undefined && text.trim() !== '' ? { text } : {}),
  });
  if (!parsed.success) return null;
  return Object.keys(parsed.data).length === 0 ? null : parsed.data;
}

/**
 * A typed reference to what a tool result touched. `app` is the plugin it belongs to (its
 * reverse-domain name, `com.example.github`), which gives its icon, its title and whether it docks
 * (an app or an editor) or opens at its service (a service). `id` is the plugin's own id of it:
 * an artifact's index id or path, a mail's id, `owner/repo#12`, a conversation's id for a task.
 */
export const toolRefSchema = z.object({
  kind: z.enum(TOOL_REF_KINDS),
  app: z.string().min(1).max(128),
  /** The plugin's type of it: an artifact type (`design/page`), or `mail`, `pull-request`… */
  type: z.string().min(1).max(128).optional(),
  id: z.string().min(1).max(512),
  title: z.string().trim().min(1).max(300),
  /** Where it lives at its service (↗), or the app's own address of it. */
  url: webUrl.optional(),
  /**
   * Its path in its app's page (`/files/<id>`, `/?item=<id>#/runs/<id>`): where the app's tab,
   * docked beside the conversation, opens on it. Absent: its `url`'s path, else the app on `id`.
   */
  route: z
    .string()
    .max(1024)
    .regex(/^\/(?![/\\])/, 'A path of the app.')
    .optional(),
  version: z.string().max(128).optional(),
  /** Its state at its service (`open`, `merged`, `draft`) or a task's (`running`, `failed`). */
  status: z.string().max(64).optional(),
  action: z.enum(TOOL_REF_ACTIONS).optional(),
  /** A line under its title: the mail's sender, the repository, the folder. */
  detail: z.string().max(300).optional(),
  preview: toolRefPreviewSchema.optional(),
  /** An artifact: the file space and path the index lists it at (`workspace`, `drive`). */
  space: z.string().max(64).optional(),
  path: z.string().max(1024).optional(),
  /** A render: the id of the source artifact it was made from. */
  derivedFrom: z.string().max(512).optional(),
});
export type ToolRef = z.infer<typeof toolRefSchema>;

/** A ref as a plugin writes it (optional fields left out). */
export type ToolRefInput = z.input<typeof toolRefSchema>;

/** One ref, de-duplicated by this key: the same thing in another call is the same row. */
export function toolRefKey(ref: Pick<ToolRef, 'kind' | 'app' | 'type' | 'id'>): string {
  return `${ref.kind}\u0000${ref.app}\u0000${ref.type ?? ''}\u0000${ref.id}`;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** The valid refs of a value (one ref or a list); anything else is ignored. */
export function parseToolRefs(value: unknown): ToolRef[] {
  const candidates = Array.isArray(value) ? value : value === undefined ? [] : [value];
  const refs: ToolRef[] = [];
  for (const candidate of candidates.slice(0, MAX_TOOL_REFS)) {
    const parsed = toolRefSchema.safeParse(candidate);
    if (parsed.success) refs.push(parsed.data);
  }
  return refs;
}

/** The refs a value carries at its top level: `ref`, `refs`, or `_meta["crowdex/refs"]`. */
function ownRefs(value: Record<string, unknown>): ToolRef[] {
  const meta = isRecord(value['_meta']) ? value['_meta'][TOOL_REFS_META_KEY] : undefined;
  return [...parseToolRefs(meta), ...parseToolRefs(value['ref']), ...parseToolRefs(value['refs'])];
}

/** A JSON object in a text, if the text is one. */
function jsonObject(text: string): Record<string, unknown> | null {
  const trimmed = text.trim();
  if (!trimmed.startsWith('{') || trimmed.length > 1_000_000) return null;
  try {
    const parsed: unknown = JSON.parse(trimmed);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * The refs of a tool's result, whatever shape reached the transcript: an MCP result (its
 * `_meta`, its structured content, its text blocks as JSON), the tool's value itself, or that
 * value as JSON text. Invalid refs are dropped; the same ref is kept once.
 */
export function toolResultRefs(result: unknown): ToolRef[] {
  const found: ToolRef[] = [];
  const visit = (value: unknown, depth: number) => {
    if (found.length >= MAX_TOOL_REFS || depth > 2) return;
    if (typeof value === 'string') {
      const parsed = jsonObject(value);
      if (parsed !== null) visit(parsed, depth + 1);
      return;
    }
    if (Array.isArray(value)) {
      // MCP content blocks: their text may be the tool's value as JSON.
      for (const block of value.slice(0, 8))
        if (isRecord(block) && block['type'] === 'text') visit(block['text'], depth + 1);
      return;
    }
    if (!isRecord(value)) return;
    found.push(...ownRefs(value));
    if (isRecord(value['structuredContent'])) found.push(...ownRefs(value['structuredContent']));
    else if (Array.isArray(value['content'])) visit(value['content'], depth + 1);
  };
  visit(result, 0);
  const seen = new Set<string>();
  return found.filter((ref) => {
    const key = toolRefKey(ref);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
